import React, { useRef } from 'react';
import moment from 'moment';
import {
  CHAT_TYPING_TIME_MS,
  messageStrings,
  ONE_MB,
} from '../../../constants';
import { commonStrings } from '../../../constants/strings';
import useToast from '../../../hooks/useToast';
import { useMessageStore } from '../../../store';
import { useUnsavedInputGuard } from '../../OrganizationScope/context';
import { EmojiPicker } from '../../EmojiPicker';
import { Icon } from '../../Icon';
import { IconButton } from '../../IconButton';
import { FileView } from '../../FileView';
import { InputFile } from '../../InputFile';
import { TextField } from '../../TextField';
import {
  CHAT_FILE_SIZE_LIMIT,
  CHAT_INPUT_ACCEPT,
  getChatFileKind,
  isAcceptedChatFileType,
  isChatDocumentKind,
  normalizeChatFile,
} from '../constants';

type Props = {
  sendMessage: (message: string, file?: File) => Promise<void>;
  onTyping: (status: boolean) => void;
  typingList: string[];
  loading?: boolean;
  channel: string;
  enableAi?: boolean;
  aiSelectedCount?: number;
  aiLoading?: boolean;
  onAiGenerate?: () => void;
};

let timeout: NodeJS.Timeout;

export function MessageInput({
  sendMessage,
  onTyping,
  typingList,
  loading = false,
  channel,
  enableAi = false,
  aiSelectedCount = 0,
  aiLoading = false,
  onAiGenerate,
}: Props) {
  // Hooks
  const ref = React.useRef(true);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const {
    draftMessages,
    setDraftMessages,
    removeDraftMessage,
    setDraftFile,
    draftFiles,
    removeDraftFile,
    draftStorageKey,
  } = useMessageStore();
  const isFirst = useRef(true);
  // ログアウトはアンマウントより先にストアを初期化するため、最後の保存先を控えておく
  const draftKeyRef = useRef(draftStorageKey);
  const discardedRef = useRef(false);
  const { toastError } = useToast();

  // States
  const [message, setMessage] = React.useState<string>(
    draftMessages?.[channel]?.message || '',
  );
  const [files, setFiles] = React.useState<File[]>(draftFiles?.[channel] || []);
  const [isTyping, setIsTyping] = React.useState<boolean>(false);
  const [isSending, setIsSending] = React.useState<boolean>(false);

  // Memo, callback
  const onChangeFiles = React.useCallback(
    (selected: File[]) => {
      if (isSending) return;
      // Windows 等で .zip に付く別名 MIME を正規 MIME に寄せてから検証・保持する
      // （以降の送信経路は File.type === 'application/zip' しか見ない）
      const files = selected.map(normalizeChatFile);
      const file = files[0];
      if (file && !isAcceptedChatFileType(file.type)) {
        return toastError(new Error(messageStrings.unsupportedFileType));
      }
      if (file) {
        const limit = CHAT_FILE_SIZE_LIMIT[getChatFileKind(file.type)];
        if (file.size > limit.maxBytes) {
          return toastError(
            new Error(
              limit.message.replace('$size', `${limit.maxBytes / ONE_MB}mb`),
            ),
          );
        }
      }
      setFiles(files);
      setDraftFile({ [channel]: files });
    },
    [channel, isSending, setDraftFile, toastError],
  );

  const onChangeMessage = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (isSending) return;
      setMessage(event.target.value);
      if (!event.target.value.trim()) {
        removeDraftMessage(channel);
      }
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        setIsTyping(false);
      }, CHAT_TYPING_TIME_MS);
      setIsTyping(true);
    },
    [channel, isSending, removeDraftMessage],
  );

  const onEmojiSelect = React.useCallback(
    (emoji: { native: string }) => {
      if (isSending) return;
      setMessage((prev) => prev + emoji.native);
    },
    [isSending],
  );

  React.useEffect(() => {
    if (!ref.current) {
      onTyping(isTyping);
    } else ref.current = false;
    return () => {};
  }, [isTyping, onTyping]);

  const onSendMessage = React.useCallback(() => {
    if (isSending) return;
    if (inputRef.current) inputRef.current.focus();
    if (message.trim() || files.length) {
      setIsSending(true);
      sendMessage(message?.trim(), files?.[0])
        .then(() => {
          // 送信が成功してから下書きと入力をクリアする
          removeDraftFile(channel);
          removeDraftMessage(channel);
          onTyping(false);
          setMessage('');
          setFiles([]);
        })
        .catch(() => {
          // 失敗時は入力・添付・下書きを保持して再送できるようにする（エラー表示は Chat 側）
        })
        .finally(() => {
          setIsSending(false);
        });
    }
  }, [
    isSending,
    message,
    files,
    removeDraftFile,
    channel,
    removeDraftMessage,
    sendMessage,
    onTyping,
  ]);

  const handleKeyUp = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
      }
    },
    [],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && event.shiftKey) {
        event.preventDefault();
        if (!isSending) {
          onSendMessage();
        }
      }
    },
    [isSending, onSendMessage],
  );

  // 未送信の入力・添付がある間は組織切り替えの前に破棄確認を挟ませる
  useUnsavedInputGuard(
    () => Boolean(message.trim()) || files.length > 0,
    () => {
      discardedRef.current = true;
    },
  );

  React.useEffect(() => {
    if (draftStorageKey) draftKeyRef.current = draftStorageKey;
  }, [draftStorageKey]);

  React.useEffect(() => {
    isFirst.current = true;
    return () => {
      isFirst.current = false;
    };
  }, []);

  React.useEffect(() => {
    return () => {
      const newDraftMessage = {
        [channel]: {
          message: message,
          timestamp: moment().valueOf(),
        },
      };
      if (message.trim() && !isFirst.current && !discardedRef.current) {
        onTyping(false);
        setDraftMessages(newDraftMessage, draftKeyRef.current);
      }
    };
  }, [channel, message, onTyping, setDraftMessages]);

  return (
    <div>
      {!!files?.length && (
        <div className="flex gap-4 overflow-x-auto p-2 h-36">
          {files.map((file, index) =>
            // PDF / ZIP は画像・動画プレビュー（FileView）ではなくファイル名チップで表示する
            isChatDocumentKind(getChatFileKind(file.type)) ? (
              <div
                key={index}
                data-testid="message-document-chip"
                className="h-full relative flex-shrink-0 flex items-center gap-2 rounded-lg border border-neutral-200 px-4 pr-10"
              >
                <IconButton
                  onClick={() => {
                    if (isSending) return;
                    setFiles((prev) => prev.filter((_, idx) => idx !== index));
                    removeDraftFile(channel);
                  }}
                  icon="close"
                  ariaLabel={commonStrings.actions.delete}
                  className="absolute top-0 right-0 z-[1]"
                  disabled={isSending}
                  transparent
                />
                <Icon icon="attach" width={20} height={20} className="shrink-0" />
                <span className="text-sm break-all line-clamp-2">
                  {file.name}
                </span>
              </div>
            ) : (
              <FileView
                className="h-full"
                key={index}
                file={file}
                onRemove={
                  isSending
                    ? undefined
                    : () => {
                        setFiles((prev) =>
                          prev.filter((_, idx) => idx !== index),
                        );
                        removeDraftFile(channel);
                      }
                }
              />
            ),
          )}
        </div>
      )}
      {!!typingList.length && (
        <div className="flex space-x-1 items-end px-2">
          <p className="-mt-[1px]">
            {`${typingList.join('、')}${messageStrings.isTyping}`}
          </p>
          <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.05s] mb-1" />
          <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce [animation-delay:-0.025s] mb-1" />
          <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce mb-1" />
        </div>
      )}

      <div className="flex items-end gap-1.5 border-t border-neutral-200 px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:gap-2 sm:px-4 relative">
        {loading ? (
          <>
            <div className="animate-pulse bg-gray-300 h-10 w-10 rounded-full"></div>
            <div className="animate-pulse bg-gray-300 h-10 w-10 rounded-full"></div>
            <div className="animate-pulse bg-gray-300 h-10 flex-1 rounded-2xl"></div>
            <div className="animate-pulse bg-gray-300 h-10 w-10 rounded-full"></div>
          </>
        ) : (
          <>
            <InputFile
              onChangeFiles={onChangeFiles}
              accept={CHAT_INPUT_ACCEPT}
              disabled={isSending}
              inputTestId="message-file-input"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted transition hover:bg-neutral-25 disabled:opacity-50"
            />
            <EmojiPicker onEmojiSelect={onEmojiSelect}>
              <button
                type="button"
                aria-label={commonStrings.actions.emoji}
                disabled={isSending}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted transition hover:bg-neutral-25 disabled:opacity-50"
              >
                <Icon icon="emoji" width={22} height={22} />
              </button>
            </EmojiPicker>
            <TextField
              type="textarea"
              name="message"
              className="flex-1"
              inputClass="resize-none text-base placeholder:text-sm"
              value={message}
              onChange={onChangeMessage}
              onKeyUp={handleKeyUp}
              onKeyDown={handleKeyDown}
              rows={Math.min(message.split('\n').length || 1, 5)}
              placeholder={messageStrings.inputPlaceholder}
              readOnly={isSending}
            />
            <button
              type="button"
              onClick={onSendMessage}
              disabled={isSending || (!message.trim() && !files.length)}
              aria-label={commonStrings.send}
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition ${
                !isSending && (message.trim() || files.length)
                  ? 'bg-accent text-white shadow-accent hover:bg-accent-mid'
                  : 'bg-neutral-100 text-neutral-400'
              }`}
            >
              <Icon icon="sendMessage" className="h-5 w-5" />
            </button>
            {enableAi && (
              <button
                type="button"
                onClick={onAiGenerate}
                disabled={aiSelectedCount === 0 || aiLoading}
                className={`relative flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all shrink-0 ${
                  aiSelectedCount > 0 && !aiLoading
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-sm'
                    : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                }`}
              >
                <Icon icon="aiStar" width={16} height={16} />
                <span>AI</span>
                {aiSelectedCount > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 text-[11px] font-bold rounded-full bg-white/25">
                    {aiSelectedCount}
                  </span>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
